import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getMwc, getRanting } from '../services/adminService'; // Untuk mendapatkan daftar MWC dan Ranting

const UserForm = ({ userToEdit, onSubmit, onCancel }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('RANTING'); // Default role
  const [jabatan, setJabatan] = useState('LAPANGAN'); // Default jabatan
  const [parentId, setParentId] = useState('');
  const [mwcUnits, setMwcUnits] = useState([]);
  const [rantingUnits, setRantingUnits] = useState([]);
  const [rantingId, setRantingId] = useState('');
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    const fetchMwcUnits = async () => {
      try {
        const mwcData = await getMwc();
        setMwcUnits(mwcData);
      } catch (error) {
        console.error('Error fetching MWC units:', error);
      }
    };

    const fetchRantingUnits = async () => {
      try {
        const rantingData = await getRanting();
        console.log('Fetched Ranting data:', rantingData);
        try {
          setRantingUnits(rantingData || []);
        } catch (e) {
          console.error('Error setting ranting units:', e);
        }
      } catch (error) {
        console.error('Error fetching Ranting units:', error);
      }
    };

    fetchMwcUnits();
    fetchRantingUnits();
  }, []);

  useEffect(() => {
    if (userToEdit) {
      console.log('Editing user. Role:', userToEdit.role);
      setUsername(userToEdit.username);
      setRole(userToEdit.role);
      setJabatan(userToEdit.jabatan || 'MANAJERIAL'); // Set jabatan from userToEdit
      setParentId(userToEdit.role === 'MWC' ? (userToEdit.mwcId || '') : (userToEdit.ranting?.mwc?.id || ''));
      setRantingId(userToEdit.rantingId || '');
      setPassword(''); // Password should not be pre-filled for security
    } else {
      console.log('Creating new user. Role: RANTING');
      setUsername('');
      setPassword('');
      setRole('RANTING');
      setJabatan('MANAJERIAL'); // Default jabatan
      setParentId('');
      setRantingId('');
    }
  }, [userToEdit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (role === 'MWC' && !parentId) {
      setFormError('Untuk role MWC, unit MWC harus dipilih.');
      return;
    }
    if (role === 'RANTING' && !parentId) {
      setFormError('Untuk role RANTING, MWC induk harus dipilih terlebih dahulu.');
      return;
    }
    if (role === 'RANTING' && !rantingId) {
      setFormError('Untuk role RANTING, unit Ranting harus dipilih.');
      return;
    }

    onSubmit({
      username,
      password: password || undefined,
      role,
      jabatan,
      mwcId: role === 'MWC' ? parentId : null,
      rantingId: role === 'RANTING' ? rantingId : null,
    });

    // Reset form after submission
    setUsername('');
    setPassword('');
    setRole('RANTING');
    setJabatan('MANAJERIAL');
    setParentId('');
    setRantingId('');
  };

  console.log('Current role:', role);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {userToEdit ? 'Edit Pengguna' : 'Tambah Pengguna'}
      </Typography>
      {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

      <TextField
        label="Username"
        fullWidth
        margin="normal"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />
      <TextField
        label={userToEdit ? 'Password (kosongkan jika tidak mengubah)' : 'Password'}
        type={showPassword ? 'text' : 'password'}
        fullWidth
        margin="normal"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        {...(!userToEdit && { required: true })} // Required only for new user
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        select
        label="Jabatan"
        fullWidth
        margin="normal"
        value={jabatan}
        onChange={(e) => setJabatan(e.target.value)}
        required
      >
        <MenuItem value="MANAJERIAL">MANAJERIAL</MenuItem>
        <MenuItem value="LAPANGAN">LAPANGAN</MenuItem>
      </TextField>
      <TextField
        select
        label="Role"
        fullWidth
        margin="normal"
        value={role}
        onChange={(e) => {
          console.log('Role changed to:', e.target.value);
          setRole(e.target.value);
          setParentId(''); // Reset parentId when role changes
          setRantingId(''); // Reset rantingId when role changes
          setFormError(''); // Clear form error on role change
        }}
        required
      >
        <MenuItem value="RANTING">RANTING</MenuItem>
        <MenuItem value="MWC">MWC</MenuItem>
        <MenuItem value="LAZISNU">LAZISNU</MenuItem>
      </TextField>

      {(role === 'MWC' || role === 'RANTING') && (
          <TextField
            select
            label={role === 'MWC' ? 'Pilih MWC' : 'MWC Induk'}
            fullWidth
            margin="normal"
            value={parentId}
            onChange={(e) => {
              setParentId(e.target.value);
              if (role === 'RANTING') {
                setRantingId(''); // Reset rantingId when MWC changes for RANTING role
              }
            }}
            required
          >
            {mwcUnits.map((mwc) => (
              <MenuItem key={mwc.id} value={mwc.id}>
                {mwc.name}
              </MenuItem>
            ))}
          </TextField>
      )}

      {role === 'RANTING' && (
          <TextField
            select
            label="Ranting"
            fullWidth
            margin="normal"
            value={rantingId}
            onChange={(e) => setRantingId(e.target.value)}
            required
            disabled={!parentId}
          >
            {rantingUnits
              .filter((ranting) => ranting.mwcId === parseInt(parentId)) // Filter based on selected MWC
              .map((ranting) => (
                <MenuItem key={ranting.id} value={ranting.id}>
                  {ranting.name}
                </MenuItem>
              ))}
          </TextField>
      )}

      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button type="submit" variant="contained" color="primary">
          Simpan
        </Button>
        <Button type="button" variant="outlined" onClick={onCancel}>
          Batal
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm;
